/**
 * Tender Bank feedback API — Phase 6.
 *
 * Posts (accept | reject | regenerate) events to the backend so the
 * counterfactual learning loop can build per-tenant boost weights.
 *
 * Defensive: failures are logged and silently swallowed. Feedback is
 * an augmentation; a network glitch must never surface to the bidder.
 */

import { PastTenderMatch } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL
  ? `${process.env.REACT_APP_API_BASE_URL}/api`
  : '/api';

export type FeedbackAction = 'accepted' | 'rejected' | 'regenerated';

interface RetrievalSnapshotPayload {
  chunk_id: string;
  tender_id: string;
  rank: number;
  fused_score: number;
  final_score: number;
  rerank_score?: number | null;
  match_type?: string | null;
  boost_breakdown?: Record<string, number> | null;
}

export interface FeedbackPostBody {
  question: string;
  company_id: string;
  bid_context?: Record<string, any> | null;
  config_snapshot?: Record<string, any> | null;
  retrieved: RetrievalSnapshotPayload[];
  action: FeedbackAction;
  target_chunk_id?: string | null;
  target_rank?: number | null;
  note?: string | null;
}

/**
 * Build a retrieval snapshot list from the matches the bidder is
 * looking at. We synthesise `chunk_id` from tender_id when the BE
 * didn't include it (defensive — older payloads).
 */
export function buildRetrievalSnapshots(
  matches: PastTenderMatch[],
): RetrievalSnapshotPayload[] {
  return matches.map((m, idx) => ({
    chunk_id: `${m.tender_id}#${m.chunk_index ?? 0}`,
    tender_id: m.tender_id,
    rank: idx + 1,
    fused_score: m.relevance_score ?? 0,
    final_score:
      (m.boost_breakdown && (m.boost_breakdown['_final'] as number)) ||
      m.relevance_score ||
      0,
    rerank_score: m.rerank_score ?? null,
    match_type: m.match_type ?? null,
    boost_breakdown: m.boost_breakdown ?? null,
  }));
}

const _authHeaders = (): HeadersInit => {
  const token = localStorage.getItem('access_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

export async function postTenderBankFeedback(
  body: FeedbackPostBody,
): Promise<{ success: boolean; event_id?: string }> {
  try {
    const response = await fetch(`${API_BASE_URL}/tender-bank/feedback`, {
      method: 'POST',
      headers: _authHeaders(),
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      console.warn(
        `tender_bank_feedback: ${response.status} — feedback dropped`,
      );
      return { success: false };
    }
    return await response.json();
  } catch (err) {
    console.warn('tender_bank_feedback: network error — feedback dropped', err);
    return { success: false };
  }
}
